import { useCallback, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../../../../assets";
import { ProjectAccessConstants } from "../../../../../constants";
import { CursorChangeProps, ResultType } from "../../../../../models";
import {
  SearchProjectOrganisationInvitations,
  SearchProjectOrganisationInvitationsResponse,
} from "../../../../../service/query";
import { ResultData } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { flattenObject } from "../../../../../utils";
import { getProjectAccessInviteAcceptRoute, getProjectDetailsByUuid } from "../../../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../../widget";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";

interface UseAccessTabReturnData {
  receivedColumns: DataGridColumnDefinition[];
  defaultSortingCriteriaReceived: SortColumn[];
  receivedDataIsLoading: boolean;
  receivedOnChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
  hasPermission: (permission: string) => boolean;
}

export const useAccessTabReceivedData = (): UseAccessTabReturnData => {
  const navigate = useNavigate();

  const { currentOrganisationUuid, currentUserType, hasPermission } = useAuth();

  const [receivedDataIsLoading, setReceivedDataIsLoading] = useState(true);

  const defaultSortingCriteriaReceived: SortColumn[] = [{ columnKey: "sentDate", direction: "DESC" }];

  const formatReceivedData = useCallback(
    (responseData: SearchProjectOrganisationInvitationsResponse | undefined): ResultData[] => {
      return (
        responseData?.results?.map((d) => {
          const result = flattenObject({ ...d });

          result.targetOrganisationDisplayName = d.targetOrganisation.displayName;
          result.targetOrganisationUuid = d.targetOrganisation.uuid;
          result.projectUuid = d.project.uuid;
          result.projectDisplayName = d.project.displayName;
          result.projectLink = {
            to:
              result.calculatedStatus === ProjectAccessConstants.STATUS_ACCEPTED
                ? getProjectDetailsByUuid(result.projectUuid, currentUserType)
                : undefined,
            text: result.projectDisplayName,
          } as DataGridLinkCellFormatterData;
          result.accessLink = {
            action: () => navigate(getProjectAccessInviteAcceptRoute(d.uuid, currentUserType)),
            icon: <ArrowheadRightDarkIcon width={12} height={12} />,
          } as IconCellFormatterData;
          // BR-1092: Where the calculatedStatus is returned as 'Sent', render the value as 'Pending'
          if (result.calculatedStatus === ProjectAccessConstants.STATUS_SENT) {
            result.calculatedStatus = ProjectAccessConstants.STATUS_PENDING;
          }

          return result;
        }) || []
      );
    },
    []
  );

  const receivedColumns: DataGridColumnDefinition[] = [
    { key: "sourceOrganisation.displayName", name: "Organisation", dataType: "string" },
    { key: "type", name: "Type", dataType: "string", minWidth: 90 },
    {
      key: "projectLink",
      name: "Project",
      dataType: "string",
      formatter: "link",
    },
    { key: "role", name: "Access level", dataType: "string" },
    {
      key: "sentDate",
      name: "Date received",
      dataType: "Date",
      formatter: "dateOnly",
      filterable: false,
      minWidth: 160,
    },
    {
      key: "sentByUser.fullName",
      name: "Sent by",
      dataType: "string",
      filterable: true,
      sortable: true,
    },
    {
      name: "Status",
      key: "calculatedStatus",
      dataType: "string",
      formatter: "projectAccessStatusPill",
      alignment: "center",
      minWidth: 200,
      filterable: false,
      sortable: false,
    },
    {
      key: "accessLink",
      name: "More details",
      dataType: "string",
      formatter: "icon",
      filterable: false,
      sortable: false,
      alignment: "center",
    },
  ];

  const receivedOnChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.projectLink) {
      filterCriteria.project = {
        displayName: filterCriteria.projectLink,
      };
      filterCriteria.projectLink = undefined;
    }

    filterCriteria.targetOrganisation = {
      uuid: {
        operator: "eq",
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        value: currentOrganisationUuid!,
      },
    };

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    await SearchProjectOrganisationInvitations({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || "",
        afterCursor: paging.afterCursor || "",
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: s.key !== "projectLink" ? s.key : "project.displayName",
        direction: s.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatReceivedData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setReceivedDataIsLoading(false);
      });
    return data;
  };

  return {
    receivedColumns,
    defaultSortingCriteriaReceived,
    receivedDataIsLoading,
    receivedOnChange,
    hasPermission,
  };
};
